/**
 * SwipeGestureHook - Touch swipe gestures for drawer navigation
 *
 * Features:
 * - Swipe from left edge to open left drawer
 * - Swipe from right edge to open right drawer
 * - Swipe to close open drawers
 * - Configurable edge detection zone and swipe threshold
 * - Visual feedback during swipe with peek animation
 *
 * Usage:
 * <div phx-hook="SwipeGestureHook"
 *      data-left-drawer-event="toggle_group_list"
 *      data-right-drawer-event="toggle_thread_list"
 *      data-left-drawer-open={@show_group_list}
 *      data-right-drawer-open={@show_thread_list}>
 */
const SwipeGestureHook = {
  mounted() {
    // Configuration
    this.edgeZone = 30; // pixels from edge to trigger edge swipe
    this.swipeThreshold = 50; // minimum distance to register as swipe
    this.velocityThreshold = 0.3; // minimum velocity for quick swipes

    // State
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchStartTime = 0;
    this.isEdgeSwipe = false;
    this.swipeDirection = null; // 'left' or 'right'
    this.peekElement = null;

    // Get drawer events from data attributes
    this.leftDrawerEvent = this.el.dataset.leftDrawerEvent || 'toggle_group_list';
    this.rightDrawerEvent = this.el.dataset.rightDrawerEvent || 'toggle_thread_list';

    // Bind event handlers
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);

    // Attach listeners
    this.el.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    this.el.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.el.addEventListener('touchend', this.handleTouchEnd, { passive: true });

    // Create peek indicator elements
    this.createPeekIndicators();
  },

  destroyed() {
    this.el.removeEventListener('touchstart', this.handleTouchStart);
    this.el.removeEventListener('touchmove', this.handleTouchMove);
    this.el.removeEventListener('touchend', this.handleTouchEnd);
    this.removePeekIndicators();
  },

  createPeekIndicators() {
    // Left edge peek indicator - use inline styles for dynamic elements
    this.leftPeek = document.createElement('div');
    this.leftPeek.id = 'swipe-peek-left';
    Object.assign(this.leftPeek.style, {
      position: 'fixed',
      left: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '1px',
      height: '80px',
      backgroundColor: '#0c99eb', // isx-babyblue-500
      borderRadius: '0 4px 4px 0',
      opacity: '0',
      transition: 'all 200ms ease-out',
      zIndex: '40',
      pointerEvents: 'none'
    });

    // Right edge peek indicator
    this.rightPeek = document.createElement('div');
    this.rightPeek.id = 'swipe-peek-right';
    Object.assign(this.rightPeek.style, {
      position: 'fixed',
      right: '0',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '1px',
      height: '80px',
      backgroundColor: '#0c99eb', // isx-babyblue-500
      borderRadius: '4px 0 0 4px',
      opacity: '0',
      transition: 'all 200ms ease-out',
      zIndex: '40',
      pointerEvents: 'none'
    });

    document.body.appendChild(this.leftPeek);
    document.body.appendChild(this.rightPeek);
  },

  removePeekIndicators() {
    if (this.leftPeek && this.leftPeek.parentNode) {
      this.leftPeek.parentNode.removeChild(this.leftPeek);
    }
    if (this.rightPeek && this.rightPeek.parentNode) {
      this.rightPeek.parentNode.removeChild(this.rightPeek);
    }
  },

  handleTouchStart(e) {
    const touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
    this.swipeDirection = null;

    const screenWidth = window.innerWidth;

    // Check if starting from left edge
    if (touch.clientX <= this.edgeZone) {
      this.isEdgeSwipe = true;
      this.swipeDirection = 'from-left';
      this.showPeek('left');
    }
    // Check if starting from right edge
    else if (touch.clientX >= screenWidth - this.edgeZone) {
      this.isEdgeSwipe = true;
      this.swipeDirection = 'from-right';
      this.showPeek('right');
    }
    // Check if either drawer is open (allow closing from anywhere)
    else if (this.isLeftDrawerOpen() || this.isRightDrawerOpen()) {
      this.isEdgeSwipe = true;
      this.swipeDirection = 'close';
    }
    else {
      this.isEdgeSwipe = false;
    }
  },

  handleTouchMove(e) {
    if (!this.isEdgeSwipe) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    // Only process horizontal swipes (prevent scroll hijacking)
    if (Math.abs(deltaY) > Math.abs(deltaX) * 1.5) {
      this.isEdgeSwipe = false;
      this.hidePeeks();
      return;
    }

    // Prevent default to stop scrolling during swipe
    if (Math.abs(deltaX) > 10) {
      e.preventDefault();
    }

    // Update peek indicator based on progress
    const progress = Math.min(Math.abs(deltaX) / this.swipeThreshold, 1);

    if (this.swipeDirection === 'from-left' && deltaX > 0) {
      this.updatePeek('left', progress);
    } else if (this.swipeDirection === 'from-right' && deltaX < 0) {
      this.updatePeek('right', progress);
    }
  },

  handleTouchEnd(e) {
    if (!this.isEdgeSwipe) {
      this.hidePeeks();
      return;
    }

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaTime = Date.now() - this.touchStartTime;
    const velocity = Math.abs(deltaX) / deltaTime;

    // Check if swipe meets threshold (distance or velocity)
    const meetsThreshold = Math.abs(deltaX) >= this.swipeThreshold || velocity >= this.velocityThreshold;

    if (meetsThreshold) {
      if (this.swipeDirection === 'from-left' && deltaX > 0) {
        // Swipe right from left edge - open left drawer (or close right)
        if (this.isRightDrawerOpen()) {
          this.pushEvent(this.rightDrawerEvent, {});
        } else if (!this.isLeftDrawerOpen()) {
          this.pushEvent(this.leftDrawerEvent, {});
        }
      }
      else if (this.swipeDirection === 'from-right' && deltaX < 0) {
        // Swipe left from right edge - open right drawer (or close left)
        if (this.isLeftDrawerOpen()) {
          this.pushEvent(this.leftDrawerEvent, {});
        } else if (!this.isRightDrawerOpen()) {
          this.pushEvent(this.rightDrawerEvent, {});
        }
      }
      else if (this.swipeDirection === 'close') {
        // Close whichever drawer is open
        if (this.isLeftDrawerOpen() && deltaX < -this.swipeThreshold / 2) {
          this.pushEvent(this.leftDrawerEvent, {});
        }
        if (this.isRightDrawerOpen() && deltaX > this.swipeThreshold / 2) {
          this.pushEvent(this.rightDrawerEvent, {});
        }
      }
    }

    // Reset state
    this.isEdgeSwipe = false;
    this.swipeDirection = null;
    this.hidePeeks();
  },

  isLeftDrawerOpen() {
    return this.el.dataset.leftDrawerOpen === 'true';
  },

  isRightDrawerOpen() {
    return this.el.dataset.rightDrawerOpen === 'true';
  },

  showPeek(side) {
    if (side === 'left' && this.leftPeek) {
      this.leftPeek.style.opacity = '0.5';
      this.leftPeek.style.width = '4px';
    } else if (side === 'right' && this.rightPeek) {
      this.rightPeek.style.opacity = '0.5';
      this.rightPeek.style.width = '4px';
    }
  },

  updatePeek(side, progress) {
    const width = 4 + (progress * 8); // 4px to 12px
    const opacity = 0.5 + (progress * 0.5); // 0.5 to 1.0

    if (side === 'left' && this.leftPeek) {
      this.leftPeek.style.width = `${width}px`;
      this.leftPeek.style.opacity = opacity.toString();
    } else if (side === 'right' && this.rightPeek) {
      this.rightPeek.style.width = `${width}px`;
      this.rightPeek.style.opacity = opacity.toString();
    }
  },

  hidePeeks() {
    if (this.leftPeek) {
      this.leftPeek.style.opacity = '0';
      this.leftPeek.style.width = '1px';
    }
    if (this.rightPeek) {
      this.rightPeek.style.opacity = '0';
      this.rightPeek.style.width = '1px';
    }
  }
};

export default SwipeGestureHook;
